import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Download, Upload, ShieldCheck } from "lucide-react";

const AdminBackup = () => {
  const { t } = useLang();
  const fileRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState("merge");
  const [lastResult, setLastResult] = useState(null);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `amazypet-backup-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("admin.backupDownloaded"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backup failed");
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(t("admin.restoreConfirm"))) {
      e.target.value = "";
      return;
    }
    setUploading(true);
    setLastResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/admin/restore?mode=${mode}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastResult(res.data);
      toast.success(t("admin.restoreDone"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-backup-panel">
      <div className="bg-white dark:bg-[#212121] border border-[#EBE5DF] dark:border-[#383838] p-8">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 bg-[#D4AF37]/10 text-[#7A6017] dark:text-[#D4AF37] rounded">
            <ShieldCheck size={22} />
          </div>
          <div className="flex-1">
            <h3 className="font-serif text-2xl mb-1 text-[#2C1E16] dark:text-[#FFFFFF]">
              {t("admin.backupTitle")}
            </h3>
            <p className="text-sm text-[#736B66] dark:text-[#B3B3B3]">
              {t("admin.backupDesc")}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="border border-[#EBE5DF] dark:border-[#383838] p-5">
            <h4 className="text-sm tracking-widest uppercase font-semibold mb-2 text-[#2C1E16] dark:text-[#FFFFFF]">
              {t("admin.downloadBackup")}
            </h4>
            <p className="text-xs text-[#736B66] dark:text-[#B3B3B3] mb-4">
              {t("admin.downloadBackupDesc")}
            </p>
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="luxury-btn w-full"
              data-testid="backup-download-btn"
            >
              <Download size={16} />
              {downloading ? "..." : t("admin.downloadBackup")}
            </button>
          </div>

          <div className="border border-[#EBE5DF] dark:border-[#383838] p-5">
            <h4 className="text-sm tracking-widest uppercase font-semibold mb-2 text-[#2C1E16] dark:text-[#FFFFFF]">
              {t("admin.restoreBackup")}
            </h4>
            <p className="text-xs text-[#736B66] dark:text-[#B3B3B3] mb-3">
              {t("admin.restoreBackupDesc")}
            </p>
            <div className="mb-3">
              <label className="text-xs text-[#736B66] dark:text-[#B3B3B3] block mb-1">
                {t("admin.restoreMode")}
              </label>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                className="luxury-input-box w-full"
                data-testid="backup-mode-select"
              >
                <option value="merge">{t("admin.modeMerge")}</option>
                <option value="replace">{t("admin.modeReplace")}</option>
              </select>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              onChange={handleUpload}
              className="hidden"
              data-testid="backup-file-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="luxury-btn-outline w-full"
              data-testid="backup-restore-btn"
            >
              <Upload size={16} />
              {uploading ? "..." : t("admin.uploadBackup")}
            </button>
          </div>
        </div>

        {lastResult && (
          <div className="mt-6 p-4 bg-[#F5F2ED] dark:bg-[#1C1C1C] border border-[#EBE5DF] dark:border-[#383838]" data-testid="backup-result">
            <p className="text-xs tracking-widest uppercase font-semibold mb-2 text-[#2C1E16] dark:text-[#FFFFFF]">
              {t("admin.restoreSummary")} ({lastResult.mode})
            </p>
            <ul className="text-xs text-[#736B66] dark:text-[#B3B3B3] grid grid-cols-2 gap-1">
              {Object.entries(lastResult.restored || {}).map(([k, v]) => (
                <li key={k}>
                  <span className="font-mono">{k}</span>: {v}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBackup;
